export const isChecklistCustomFieldSelected = (customField) => {
    if (!customField) {
        return false
    }

    if (customField.type === 'C') {
        return customField.content?.active === true
    }

    if (customField.type === 'S') {
        const value = customField.content?.value
        return value !== null && value !== undefined && value !== '' && String(value).trim() !== ''
    }

    if (customField.type === 'M') {
        const values = customField.content?.values
        return Array.isArray(values) && values.length > 0
    }

    return false
}

const normalizeRuleValue = (value) => {
    if (value === null || value === undefined) {
        return null
    }

    return String(value)
}

const getNormalizedTriggerValues = (rule) => {
    return new Set(
        (rule?.trigger_values || [])
            .map(normalizeRuleValue)
            .filter(value => value !== null)
    )
}

export const doesConditionalRuleMatch = (rule, amoValuesByFieldId = {}) => {
    if (!rule?.amo_field_id) {
        return false
    }

    const triggerValues = getNormalizedTriggerValues(rule)
    if (triggerValues.size === 0) {
        return false
    }

    const currentValue = amoValuesByFieldId[String(rule.amo_field_id)]
    if (Array.isArray(currentValue)) {
        return currentValue
            .map(normalizeRuleValue)
            .filter(value => value !== null)
            .some(value => triggerValues.has(value))
    }

    const normalizedCurrentValue = normalizeRuleValue(currentValue)
    if (normalizedCurrentValue === null) {
        return false
    }

    return triggerValues.has(normalizedCurrentValue)
}

export const getExcludedConditionalRequiredFieldIds = (
    rules = [],
    amoValuesByFieldId = {}
) => {
    const excludedFieldIds = new Set()

    rules.forEach(rule => {
        if (!doesConditionalRuleMatch(rule, amoValuesByFieldId)) {
            return
        }

        ;(rule.excluded_custom_field_ids || []).forEach(fieldId => {
            if (fieldId !== null && fieldId !== undefined) {
                excludedFieldIds.add(fieldId)
            }
        })
    })

    return excludedFieldIds
}

const getChecklistLimit = (checklist) => {
    const rawLimit = checklist?.visual_settings?.count_of_cfs_full
    if (rawLimit === null || rawLimit === undefined || rawLimit === '') {
        return null
    }

    const numericLimit = Number(rawLimit)
    if (!Number.isFinite(numericLimit) || numericLimit <= 0) {
        return null
    }

    return numericLimit
}

export const getChecklistConditionalRequirementState = (
    checklist,
    amoValuesByFieldId = {}
) => {
    const customFields = checklist?.custom_fields || []
    const excludedFieldIds = getExcludedConditionalRequiredFieldIds(
        checklist?.conditional_required_rules || [],
        amoValuesByFieldId
    )
    const effectiveRequiredFields = customFields.filter(
        customField => !excludedFieldIds.has(customField.id)
    )
    const effectiveRequiredCount = effectiveRequiredFields.length
    const checklistLimit = getChecklistLimit(checklist)
    const denominator = checklistLimit === null
        ? effectiveRequiredCount
        : Math.min(checklistLimit, effectiveRequiredCount)
    const selectedEffectiveCount = effectiveRequiredFields.filter(
        isChecklistCustomFieldSelected
    ).length
    const cappedSelectedCount = denominator > 0
        ? Math.min(selectedEffectiveCount, denominator)
        : 0

    return {
        excludedFieldIds,
        effectiveRequiredFields,
        effectiveRequiredCount,
        denominator,
        selectedEffectiveCount,
        cappedSelectedCount,
    }
}

export const shouldDisableChecklistField = (
    checklist,
    customFieldId,
    amoValuesByFieldId = {}
) => {
    const customField = (checklist?.custom_fields || []).find(
        field => field.id === customFieldId
    )
    if (!customField) {
        return false
    }

    const requirementState = getChecklistConditionalRequirementState(
        checklist,
        amoValuesByFieldId
    )

    if (requirementState.excludedFieldIds.has(customFieldId)) {
        return false
    }

    if (isChecklistCustomFieldSelected(customField)) {
        return false
    }

    if (!requirementState.denominator) {
        return false
    }

    return requirementState.selectedEffectiveCount >= requirementState.denominator
}

export const buildConditionalRequiredAmoValuesMap = (
    checklists = [],
    getAmoFieldValue = () => null
) => {
    const amoFieldIds = new Set()

    checklists.forEach(checklist => {
        ;(checklist?.conditional_required_rules || []).forEach(rule => {
            if (rule?.amo_field_id !== null && rule?.amo_field_id !== undefined) {
                amoFieldIds.add(String(rule.amo_field_id))
            }
        })
    })

    const amoValuesByFieldId = {}
    amoFieldIds.forEach(fieldId => {
        amoValuesByFieldId[fieldId] = getAmoFieldValue(fieldId)
    })

    return amoValuesByFieldId
}
